import React, { useEffect, useState } from 'react';
import { Flex, Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { makeSafeRequest } from '../../../../common/makeSafeRequest';
import { AssessmentScenarioData } from '../../../../types/assessments';
import { BehaviorParameter } from '../../../../types/behaviors';
import { Optional } from '../../../../types/common';
import { AreasData } from '../../../../types/geometries';
import { AreasEditor } from './areas-editor/AreasEditor';
import { AreasSelector } from './areas-selector/AreasSelector';

interface IAreasManager {
	value: number[];
	scenarioSettings: Optional<AssessmentScenarioData, 'id'>;
	areasData?: AreasData | null;
	reloadAreas?: () => void;
	onChange: (value: number[]) => void;
	setSetting?: (
		settingName: keyof AssessmentScenarioData,
		value: AssessmentScenarioData[keyof AssessmentScenarioData]
	) => void;
}

// Reset any DEPLOYMENT_ZONE parameter pointing at a removed area back to the
// "no zone" default (-1) so echelons don't keep a stale reference to a zone that
// was deleted or deselected, which the sim rejects.
const resetDeploymentZoneInParams = (
	params: BehaviorParameter[],
	isRemovedZone: (areaId: number) => boolean
): { params: BehaviorParameter[]; changed: boolean } => {
	let changed = false;

	const nextParams = params.map((param) => {
		let updated = param;

		if (param.property === 'DEPLOYMENT_ZONE' && isRemovedZone(Number(param.value))) {
			updated = { ...updated, value: -1 };
			changed = true;
		}

		if (updated.settings) {
			const nested = resetDeploymentZoneInParams(updated.settings, isRemovedZone);
			if (nested.changed) {
				updated = { ...updated, settings: nested.params };
				changed = true;
			}
		}

		return updated;
	});

	return { params: nextParams, changed };
};

const resetDeploymentZonesForSide = (
	echelonSettings: Record<number, BehaviorParameter[]> | undefined,
	isRemovedZone: (areaId: number) => boolean
): Record<number, BehaviorParameter[]> | null => {
	if (!echelonSettings) return null;

	let changed = false;
	const next: Record<number, BehaviorParameter[]> = {};

	for (const [echelonId, params] of Object.entries(echelonSettings)) {
		const result = resetDeploymentZoneInParams(params, isRemovedZone);
		next[Number(echelonId)] = result.params;
		if (result.changed) changed = true;
	}

	return changed ? next : null;
};

export const AreasManager: React.FC<IAreasManager> = ({
	value,
	scenarioSettings,
	areasData: externalAreasData,
	reloadAreas: externalReloadAreas,
	onChange,
	setSetting,
}) => {
	const [internalAreasData, setInternalAreasData] = useState<AreasData>();
	const [reloadData, setReloadData] = useState(true);
	const usesExternal = externalAreasData !== undefined;
	const areasData = usesExternal ? externalAreasData : internalAreasData;

	const triggerReload = () => {
		if (externalReloadAreas) externalReloadAreas();
		else setReloadData(true);
	};

	// Reset every echelon DEPLOYMENT_ZONE pointing at a removed zone back to -1.
	const resetDeploymentZonesForRemovedAreas = (isRemovedZone: (areaId: number) => boolean) => {
		const updatedBlue = resetDeploymentZonesForSide(
			scenarioSettings.blue_echelon_settings,
			isRemovedZone
		);
		if (updatedBlue && setSetting) setSetting('blue_echelon_settings', updatedBlue);

		const updatedRed = resetDeploymentZonesForSide(
			scenarioSettings.red_echelon_settings,
			isRemovedZone
		);
		if (updatedRed && setSetting) setSetting('red_echelon_settings', updatedRed);
	};

	// When zones get deselected in the checkbox, clear any echelon deployment zone
	// that pointed at them before propagating the new selection.
	const handleZonesChange = (newValue: number[]) => {
		const removedIds = value.filter((id) => !newValue.includes(id));
		if (removedIds.length > 0) {
			resetDeploymentZonesForRemovedAreas((id) => removedIds.includes(id));
		}
		onChange(newValue);
	};

	const addArea = async (
		areaProperties: {
			id: number;
			type: string;
			label: string;
			owner: string;
			corridor: string;
		},
		geometry: any
	) => {
		return await makeSafeRequest<{ status: string }>('areas/', 'POST', {
			payload: {
				properties: areaProperties,
				geometry: geometry,
				geography: scenarioSettings.geography.toLowerCase(),
				resolution: scenarioSettings.resolution.toLowerCase(),
			},
			onSuccessMessage: 'Area added successfully!',
			onSuccess: (data) => {
				triggerReload();
			},
		});
	};

	const deleteArea = async (areaId: number) => {
		return await makeSafeRequest<{ status: string }>(`areas/${areaId}/`, 'DELETE', {
			payload: {
				geography: scenarioSettings.geography.toLowerCase(),
				resolution: scenarioSettings.resolution.toLowerCase(),
			},
			onSuccessMessage: 'Area deleted successfully!',
			onErrorMessage: 'Failed to delete area',
			onSuccess: () => {
				resetDeploymentZonesForRemovedAreas((id) => id === areaId);
				// Drop the deleted zone from the current selection so it doesn't
				// linger as a stale id in scenarioSettings.zones.
				if (value.includes(areaId)) {
					onChange(value.filter((id) => id !== areaId));
				}
				triggerReload();
			},
		});
	};

	const editArea = async (
		areaId: number,
		areaProperties: { id: number; type: string; label: string; owner: string; corridor: string }
	) => {
		return await makeSafeRequest<{ status: string }>(`areas/${areaId}/`, 'PUT', {
			payload: {
				properties: areaProperties,
				geography: scenarioSettings.geography.toLowerCase(),
				resolution: scenarioSettings.resolution.toLowerCase(),
			},
			onSuccessMessage: 'Area edited successfully!',
			onErrorMessage: 'Failed to edit area',
			onSuccess: (data) => {
				triggerReload();
			},
		});
	};

	useEffect(() => {
		if (usesExternal) return;

		const downloadAreas = async () => {
			return await makeSafeRequest<AreasData>(`areas/`, 'GET', {
				params: {
					geography: scenarioSettings.geography.toLowerCase(),
					resolution: scenarioSettings.resolution.toLowerCase(),
				},
				onSuccess: (json) => {
					if (json) {
						setReloadData(false);
						setInternalAreasData(json);
					} else {
						notifications.show({
							title: 'Error',
							message: 'No areas found for given geography and resolution',
							color: 'red',
							autoClose: 5000,
						});
					}
				},
			});
		};

		if (reloadData === true) {
			downloadAreas();
		}
	}, [reloadData, scenarioSettings.geography, scenarioSettings.resolution, usesExternal]);

	return (
		<Flex gap={'1rem'} direction="column">
			{areasData ? (
				<>
					<AreasSelector value={value} areasData={areasData} onChange={handleZonesChange} />
					<AreasEditor
						value={value}
						areasData={areasData}
						addArea={addArea}
						deleteArea={deleteArea}
						editArea={editArea}
						onChange={handleZonesChange}
					/>
				</>
			) : (
				<Loader />
			)}
		</Flex>
	);
};
